import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ChevronLeftIcon, ExclamationCircleIcon, LockClosedIcon, PencilIcon } from '@heroicons/react/24/outline';
import Drawer from '../Drawer';
import { arrayHasItems, getAllowedUserPermissions } from '../../helpers/common';
import {
  CHILDREN_TAB_BUDGET_X_FORECAST,
  CHILDREN_TAB_BUDGET_TEMPLATE_SHEET_LOAD,
} from '../../consts/management/users/users';
import { LoaderSpinner } from '../LoaderSpinner';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import {
  BudgetLoadingKeys,
  createBudgetCategory,
  createBudgetTemplate,
  createBudgetNetIncome,
  deleteBudgetCategory,
  deleteBudgetNetIncome,
  deleteBudgetTemplate,
  getBudgetCards,
  getBudgetSettings,
  getBudgetDrivers,
  getBudgetSource,
  setBudgetDriver,
  recalculateBudgetDrivers,
  setBudgetPeriodStatus,
  updateBudgetTemplate,
  updateBudgetNetIncome,
  updateCategoryPositions,
} from '../../slices/quickbooks/budgetSlice';
import { EditableCategoryName } from './EditableCategoryName';
import { EditableTemplateName } from './EditableTemplateName';
import { EditableNetIncomeName } from './EditableNetIncomeName';
import { ConfrimModal } from '../ConfrimModal';
import { CategoryDeleteInfoModal } from './CategoryDeleteInfoModal';
import { AddCategoryModal } from './AddCategoryModal';
import { TemplateCreateEditSidebar } from './TemplateCreateEditSidebar';
import { NetIncomeCreateEditSidebar } from './NetIncomeCreateEditSidebar';
import { NetIncomeDeleteInfoModal } from './NetIncomeDeleteInfoModal';
import BudgetTemplateSheetLoadModal from './BudgetTemplateSheetLoadModal';
import { toast } from 'react-toastify';

const PANELS = {
  LIST: 'list',
  CATEGORIES: 'categories',
  TEMPLATES: 'templates',
  TEMPLATE_EDITOR: 'template_editor',
  NET_INCOME: 'net_income',
  NET_INCOME_EDITOR: 'net_income_editor',
  DRIVERS: 'drivers',
};

const DRIVER_MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const formatDisplayDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`
    : 'unknown date';
};
const formatBudgetMonth = (value) => {
  const match = String(value || '').slice(0, 7).match(/^(\d{4})-(\d{2})$/);
  return match ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }) : String(value || 'Unknown month');
};
const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(value) || 0);

export const BudgetSettingsSidebar = ({ isOpen, onClose, marginFromSidebar, onCategoryChange, onReviewChanges, budgetId, openPanel, onPanelOpened }) => {
  const dispatch = useDispatch();
  const { currentUser, loading: authLoading } = useSelector((state) => state.auth);
  const {
    budgetSettings,
    budgetCards,
    budgetDrivers,
    budgetSource,
    loadingKeys: budgetSettingsLoadingKeys,
  } = useSelector((state) => state.budget);

  const userPermissions = currentUser?.permissions || [];
  const { childIdToAccess } = getAllowedUserPermissions({ permissions: userPermissions });
  const routesAccess = childIdToAccess.get(CHILDREN_TAB_BUDGET_X_FORECAST);
  const hasEditAccess = routesAccess === 'edit';
  const sheetLoadAccess = childIdToAccess.get(CHILDREN_TAB_BUDGET_TEMPLATE_SHEET_LOAD);
  const hasSheetLoadAccess = sheetLoadAccess === 'view' || sheetLoadAccess === 'edit';
  const hasSheetLoadEditAccess = sheetLoadAccess === 'edit';

  const [panel, setPanel] = useState(PANELS.LIST);
  const [driverInputs, setDriverInputs] = useState({});
  const [pendingDriverEdit, setPendingDriverEdit] = useState(null);
  const [pendingPeriodChange, setPendingPeriodChange] = useState(null);
  const [showTieOutDetails, setShowTieOutDetails] = useState(false);
  const [sheetLoadOpen, setSheetLoadOpen] = useState(false);

  // Categories local ordering state (same behavior as BudgetSettings page)
  const [orderedCategories, setOrderedCategories] = useState([]);
  const [isLocalOrdering, setIsLocalOrdering] = useState(false);
  const categoriesToRender = isLocalOrdering ? orderedCategories : (budgetSettings?.categories || []);

  // Categories delete + add modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteInfoModalOpen, setDeleteInfoModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);

  // Templates delete + editor state
  const templatesToRender = budgetSettings?.templates || [];
  const [deleteTemplateModalOpen, setDeleteTemplateModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [templateEditorMode, setTemplateEditorMode] = useState('create'); // create | edit
  const [templateToEdit, setTemplateToEdit] = useState(null);

  // Template editor submit wiring (embed template editor inside this drawer)
  const [templateEditorCanSave, setTemplateEditorCanSave] = useState(false);
  const [templateEditorSubmit, setTemplateEditorSubmit] = useState(null);

  // Net Income Calculation list + editor state
  const netIncomeCalculationsToRender = budgetSettings?.netIncomeCalculations || [];
  const [deleteNetIncomeModalOpen, setDeleteNetIncomeModalOpen] = useState(false);
  const [deleteNetIncomeInfoModalOpen, setDeleteNetIncomeInfoModalOpen] = useState(false);
  const [netIncomeToDelete, setNetIncomeToDelete] = useState(null);
  const [netIncomeEditorMode, setNetIncomeEditorMode] = useState('create'); // create | edit
  const [netIncomeToEdit, setNetIncomeToEdit] = useState(null);

  // Net income editor submit wiring (embed editor inside this drawer)
  const [netIncomeEditorCanSave, setNetIncomeEditorCanSave] = useState(false);
  const [netIncomeEditorSubmit, setNetIncomeEditorSubmit] = useState(null);
  const demandPlanSectionRef = useRef(null);

  const budgetSettingsIsLoading = useCallback(() => {
    return budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.getBudgetSettings);
  }, [budgetSettingsLoadingKeys]);

  // Load template metadata for either template-management or sheet-load access.
  useEffect(() => {
    if (!isOpen) return;
    if (!hasEditAccess && !hasSheetLoadAccess) return;
    dispatch(getBudgetSettings({}));
    if (hasSheetLoadAccess) dispatch(getBudgetCards({}));
  }, [dispatch, isOpen, hasEditAccess, hasSheetLoadAccess]);

  useEffect(() => {
    if (isOpen && hasEditAccess && budgetId) dispatch(getBudgetDrivers({ budgetId }));
  }, [dispatch, isOpen, hasEditAccess, budgetId]);

  useEffect(() => {
    if (isOpen && hasEditAccess && budgetId && panel === PANELS.DRIVERS) dispatch(getBudgetSource(budgetId));
  }, [dispatch, isOpen, hasEditAccess, budgetId, panel]);

  useEffect(() => {
    if (!isOpen || openPanel !== 'drivers') return;
    setPanel(PANELS.DRIVERS);
    onPanelOpened?.();
  }, [isOpen, openPanel, onPanelOpened]);

  useEffect(() => {
    if (isOpen && panel === PANELS.LIST) {
      demandPlanSectionRef.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [isOpen, panel]);

  // Keep orderedCategories in sync
  useEffect(() => {
    if (!isOpen) return;
    if (!isLocalOrdering && arrayHasItems(budgetSettings?.categories)) {
      setOrderedCategories([...budgetSettings.categories]);
    }
  }, [isOpen, budgetSettings?.categories, isLocalOrdering]);

  // Reset panel when closing
  useEffect(() => {
    if (isOpen) return;
    setPanel(PANELS.LIST);
    setIsLocalOrdering(false);
    setDeleteModalOpen(false);
    setDeleteInfoModalOpen(false);
    setCategoryToDelete(null);
    setAddCategoryModalOpen(false);
    setDeleteTemplateModalOpen(false);
    setTemplateToDelete(null);
    setTemplateEditorMode('create');
    setTemplateToEdit(null);
    setTemplateEditorCanSave(false);
    setTemplateEditorSubmit(null);
    setDeleteNetIncomeModalOpen(false);
    setDeleteNetIncomeInfoModalOpen(false);
    setNetIncomeToDelete(null);
    setNetIncomeEditorMode('create');
    setNetIncomeToEdit(null);
    setNetIncomeEditorCanSave(false);
    setNetIncomeEditorSubmit(null);
    setSheetLoadOpen(false);
  }, [isOpen]);

  const moveCategory = useCallback(
    (dragIndex, hoverIndex) => {
      if (!hasEditAccess) return;
      setIsLocalOrdering(true);
      setOrderedCategories((prevCategories) => {
        const updated = [...prevCategories];
        const [removed] = updated.splice(dragIndex, 1);
        updated.splice(hoverIndex, 0, removed);
        return updated;
      });
    },
    [hasEditAccess]
  );

  const persistOrderIfChanged = useCallback(async () => {
    if (!hasEditAccess || !arrayHasItems(orderedCategories)) return;

    const positionUpdates = orderedCategories.map((category, index) => ({
      id: category.id,
      position: index + 1,
    }));

    try {
      await dispatch(updateCategoryPositions({ positionUpdates })).unwrap();
      await dispatch(getBudgetSettings({}));
      onCategoryChange?.();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLocalOrdering(false);
    }
  }, [dispatch, orderedCategories, hasEditAccess, onCategoryChange]);

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteModalOpen(true);
  };

  const handleDeleteDisabledClick = (category) => {
    setCategoryToDelete(category);
    setDeleteInfoModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    try {
      await dispatch(deleteBudgetCategory({ categoryId: categoryToDelete.id })).unwrap();
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
      await dispatch(getBudgetSettings({}));
      onCategoryChange?.();
    } catch (error) {
      if ((error?.budgets && error.budgets.length > 0) || error?.is_used_in_forecast_settings) {
        setDeleteModalOpen(false);
        setDeleteInfoModalOpen(true);
      } else {
        setDeleteModalOpen(false);
        setCategoryToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setCategoryToDelete(null);
  };

  const handleCloseInfoModal = () => {
    setDeleteInfoModalOpen(false);
    setCategoryToDelete(null);
  };

  const handleCreateCategory = async (name) => {
    try {
      await dispatch(createBudgetCategory({ name })).unwrap();
      setAddCategoryModalOpen(false);
      await dispatch(getBudgetSettings({}));
      onCategoryChange?.();
    } catch (error) {
      console.error(error);
    }
  };

  const openTemplatesPanel = () => setPanel(PANELS.TEMPLATES);
  const openCategoriesPanel = () => setPanel(PANELS.CATEGORIES);
  const openNetIncomePanel = () => setPanel(PANELS.NET_INCOME);
  const backToList = () => setPanel(PANELS.LIST);

  const openCreateTemplateEditor = () => {
    setTemplateEditorMode('create');
    setTemplateToEdit(null);
    setPanel(PANELS.TEMPLATE_EDITOR);
  };

  const openEditTemplateEditor = (template) => {
    setTemplateEditorMode('edit');
    setTemplateToEdit(template || null);
    setPanel(PANELS.TEMPLATE_EDITOR);
  };

  const closeTemplateEditor = () => {
    setTemplateEditorMode('create');
    setTemplateToEdit(null);
    setPanel(PANELS.TEMPLATES);
  };

  const openCreateNetIncomeEditor = () => {
    setNetIncomeEditorMode('create');
    setNetIncomeToEdit(null);
    setPanel(PANELS.NET_INCOME_EDITOR);
  };

  const openEditNetIncomeEditor = (calculation) => {
    setNetIncomeEditorMode('edit');
    setNetIncomeToEdit(calculation || null);
    setPanel(PANELS.NET_INCOME_EDITOR);
  };

  const closeNetIncomeEditor = () => {
    setNetIncomeEditorMode('create');
    setNetIncomeToEdit(null);
    setPanel(PANELS.NET_INCOME);
  };

  const handleSaveTemplate = async ({ name, categories }) => {
    try {
      if (templateEditorMode === 'edit' && templateToEdit?.id) {
        await dispatch(
          updateBudgetTemplate({
            templateId: templateToEdit.id,
            name,
            items: { categories },
          })
        ).unwrap();
      } else {
        await dispatch(
          createBudgetTemplate({
            name,
            items: { categories },
          })
        ).unwrap();
      }
      closeTemplateEditor();
      await dispatch(getBudgetSettings({}));
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteTemplateClick = (template) => {
    setTemplateToDelete(template);
    setDeleteTemplateModalOpen(true);
  };

  const handleConfirmDeleteTemplate = async () => {
    if (!templateToDelete?.id) return;
    try {
      await dispatch(deleteBudgetTemplate({ templateId: templateToDelete.id })).unwrap();
      setDeleteTemplateModalOpen(false);
      setTemplateToDelete(null);
      await dispatch(getBudgetSettings({}));
    } catch (error) {
      console.error(error);
      setDeleteTemplateModalOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleCancelDeleteTemplate = () => {
    setDeleteTemplateModalOpen(false);
    setTemplateToDelete(null);
  };

  const handleSaveNetIncome = async ({ name, items }) => {
    try {
      if (netIncomeEditorMode === 'edit' && netIncomeToEdit?.id) {
        await dispatch(updateBudgetNetIncome({ id: netIncomeToEdit.id, name, items })).unwrap();
      } else {
        await dispatch(createBudgetNetIncome({ name, items })).unwrap();
      }
      closeNetIncomeEditor();
      await dispatch(getBudgetSettings({}));
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteNetIncomeClick = (calculation) => {
    const budgets = calculation?.budgets || [];
    if (Array.isArray(budgets) && budgets.length > 0) {
      setNetIncomeToDelete(calculation);
      setDeleteNetIncomeInfoModalOpen(true);
      return;
    }
    setNetIncomeToDelete(calculation);
    setDeleteNetIncomeModalOpen(true);
  };

  const handleDeleteNetIncomeDisabledClick = (calculation) => {
    setNetIncomeToDelete(calculation);
    setDeleteNetIncomeInfoModalOpen(true);
  };

  const handleCancelDeleteNetIncome = () => {
    setDeleteNetIncomeModalOpen(false);
    setNetIncomeToDelete(null);
  };

  const handleCloseNetIncomeInfoModal = () => {
    setDeleteNetIncomeInfoModalOpen(false);
    setNetIncomeToDelete(null);
  };

  const handleConfirmDeleteNetIncome = async () => {
    if (!netIncomeToDelete?.id) return;
    try {
      await dispatch(deleteBudgetNetIncome({ id: netIncomeToDelete.id })).unwrap();
      setDeleteNetIncomeModalOpen(false);
      setNetIncomeToDelete(null);
      await dispatch(getBudgetSettings({}));
    } catch (error) {
      // If API returns budgets, show info modal (same behavior as categories)
      if (error?.budgets && error.budgets.length > 0) {
        setDeleteNetIncomeModalOpen(false);
        setDeleteNetIncomeInfoModalOpen(true);
      } else {
        console.error(error);
        setDeleteNetIncomeModalOpen(false);
        setNetIncomeToDelete(null);
      }
    }
  };

  const drawerTitle = useMemo(() => {
    if (panel === PANELS.CATEGORIES) return 'Budget Settings • Categories';
    if (panel === PANELS.TEMPLATES) return 'Budget Settings • Templates';
    if (panel === PANELS.TEMPLATE_EDITOR) return templateEditorMode === 'edit' ? 'Edit Template' : 'Create Template';
    if (panel === PANELS.NET_INCOME) return 'Budget Settings • Net Income Calculation';
    if (panel === PANELS.NET_INCOME_EDITOR) return netIncomeEditorMode === 'edit' ? 'Edit Net Income' : 'Create Net Income';
    if (panel === PANELS.DRIVERS) return 'Budget Settings • Drivers';
    return 'Budget Settings';
  }, [panel, templateEditorMode, netIncomeEditorMode]);

  const periodForMonth = (monthIndex) => (budgetSource?.periods || budgetDrivers?.periods || []).find(
    (period) => Number(String(period.month).slice(5, 7)) - 1 === monthIndex,
  );

  const isDriverMonthClosed = (monthIndex) => periodForMonth(monthIndex)?.status === 'closed';
  const driverYear = periodForMonth(0)?.month
    ? String(periodForMonth(0).month).slice(0, 4)
    : String(new Date().getFullYear());

  const openDriverEdit = (definition, scope, monthIndex, rawValue) => {
    const normalized = Number(rawValue);
    if (!Number.isFinite(normalized) || normalized < 0) return;
    const inputKey = `${definition.key}-${scope}-${DRIVER_MONTHS[monthIndex]}`;
    const current = budgetDrivers?.drivers?.[definition.key]?.[scope]?.[monthIndex];
    if (Math.abs((current == null ? 0 : current * 100) - normalized) < 0.0001) {
      setDriverInputs((values) => {
        const next = { ...values };
        delete next[inputKey];
        return next;
      });
      return;
    }
    setPendingDriverEdit({ definition, scope, monthIndex, inputKey, oldValue: current == null ? 0 : current * 100, newValue: normalized, note: '' });
  };

  const saveDriver = async () => {
    if (!pendingDriverEdit) return;
    const { definition, scope, monthIndex, inputKey, newValue, note } = pendingDriverEdit;
    await dispatch(setBudgetDriver({
      budgetId,
      driver_key: definition.key,
      scope,
      values: { [monthIndex + 1]: newValue / 100 },
      note: note?.trim() ? note.trim() : undefined,
    }));
    setPendingDriverEdit(null);
    setDriverInputs((values) => {
      const next = { ...values };
      delete next[inputKey];
      return next;
    });
    await dispatch(getBudgetDrivers({ budgetId }));
  };

  const renderDriverRow = (definition, scope) => {
    const values = budgetDrivers?.drivers?.[definition.key]?.[scope] || [];
    return (
      <tr key={`${definition.key}-${scope}`} className="border-b">
        <td className="whitespace-nowrap px-2 py-2 text-xs text-gray-700">
          {definition.label}{definition.scopeKind === 'channel' ? ` (${scope})` : ''}
        </td>
        {DRIVER_MONTHS.map((month, index) => {
          const closed = isDriverMonthClosed(index);
          const value = values[index];
          const inputKey = `${definition.key}-${scope}-${month}`;
          return (
            <td key={month} className={`relative min-w-[72px] px-1 py-2 text-center ${closed ? 'bg-gray-50 text-gray-400' : ''}`}>
              {closed ? (
                <span className="inline-flex items-center gap-1 text-gray-400" title={`Locked · ${String(index + 1).padStart(2, '0')}/${driverYear} closed ${periodForMonth(index)?.closed_at ? formatDisplayDate(periodForMonth(index).closed_at) : 'unknown date'} by ${periodForMonth(index)?.closed_by || 'system'} · reopen in Periods`}>
                  <LockClosedIcon className="mx-auto h-3.5 w-3.5 text-gray-400" />
                  <span className="text-xs">{value == null ? '—' : `${(value * 100).toFixed(2)}%`}</span>
                </span>
              ) : (
                <>
                  <input
                    aria-label={`${definition.label} ${scope} ${month}`}
                    className="w-full rounded border border-blue-100 bg-[#E8F0FE] px-1 py-1 text-right text-xs tabular-nums"
                    value={driverInputs[inputKey] ?? (value == null ? '' : (value * 100).toFixed(2))}
                    onChange={(event) => setDriverInputs((current) => ({ ...current, [inputKey]: event.target.value }))}
                    onBlur={(event) => openDriverEdit(definition, scope, index, event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                      if (event.key === 'Escape') {
                        setPendingDriverEdit(null);
                        setDriverInputs((values) => {
                          const next = { ...values };
                          delete next[inputKey];
                          return next;
                        });
                      }
                    }}
                  />
                  {pendingDriverEdit?.inputKey === inputKey && (
                    <div className="absolute z-20 mt-1 w-56 rounded border border-gray-200 bg-white p-2 text-left shadow-lg">
                      <div className="text-[11px] font-medium text-gray-700">{definition.label} · {scope} · {DRIVER_MONTHS[index].toUpperCase()} {driverYear}</div>
                      <div className="mt-1 text-xs tabular-nums text-gray-600">{pendingDriverEdit.oldValue.toFixed(2)}% → {pendingDriverEdit.newValue.toFixed(2)}%</div>
                      <input className="mt-2 w-full rounded border border-gray-300 px-2 py-1 text-xs" placeholder="Optional note" value={pendingDriverEdit.note} onChange={(event) => setPendingDriverEdit((current) => ({ ...current, note: event.target.value }))} />
                      <div className="mt-2 flex justify-end gap-2">
                        <button type="button" className="text-xs text-gray-500" onClick={() => { setPendingDriverEdit(null); setDriverInputs((values) => { const next = { ...values }; delete next[inputKey]; return next; }); }}>Cancel</button>
                        <button type="button" className="rounded bg-primary px-2 py-1 text-xs text-white" onClick={saveDriver}>Save</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  const isSavingTemplate =
    budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.createTemplate) ||
    budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.updateTemplate);

  const isSavingNetIncome =
    budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.createNetIncome) ||
    budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.updateNetIncome);

  const showActionButtons = panel === PANELS.TEMPLATE_EDITOR || panel === PANELS.NET_INCOME_EDITOR;
  const submitDisabled =
    panel === PANELS.TEMPLATE_EDITOR
      ? !templateEditorCanSave
      : panel === PANELS.NET_INCOME_EDITOR
        ? !netIncomeEditorCanSave
        : false;

  const handleDrawerSubmit = () => {
    if (panel === PANELS.TEMPLATE_EDITOR) return templateEditorSubmit?.();
    if (panel === PANELS.NET_INCOME_EDITOR) return netIncomeEditorSubmit?.();
  };

  const handleDrawerClose = () => {
    // If user is in template editor, close editor first (go back), otherwise close the whole drawer.
    if (panel === PANELS.TEMPLATE_EDITOR) return closeTemplateEditor();
    if (panel === PANELS.NET_INCOME_EDITOR) return closeNetIncomeEditor();
    if (panel === PANELS.NET_INCOME) return backToList();
    onClose?.();
  };

  if (authLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title="Budget Settings" width="900px" size="xl">
        <div className="p-6">
          <LoaderSpinner colorSpinner="black" width="70" height="70" border="3" />
        </div>
      </Drawer>
    );
  }

  if (!hasEditAccess) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title="Budget Settings" width="900px" size="xl">
        <div className="p-6">No access</div>
      </Drawer>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Drawer
        isOpen={isOpen}
        onClose={handleDrawerClose}
        title={drawerTitle}
        showActionButtons={showActionButtons}
        submitButtonText={isSavingTemplate || isSavingNetIncome ? 'Saving...' : 'Save'}
        submitDisabled={submitDisabled}
        onSubmit={handleDrawerSubmit}
        width={panel === PANELS.DRIVERS ? '1180px' : '900px'}
        size="xl"
        marginFromSidebar={marginFromSidebar}
      >
        <div className="relative">
            {budgetSettingsIsLoading() && <OverlayGrayBackground />}
            <div className="px-6 py-4">
                {panel !== PANELS.LIST && (
                  <div className="mb-4 flex items-center justify-between">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                        onClick={() => {
                            if (panel === PANELS.TEMPLATE_EDITOR) return closeTemplateEditor();
                            return backToList();
                        }}
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                        Back
                      </button>
                  </div>
                )}

                {panel === PANELS.LIST && (
                  <div className="space-y-3">
                      <section ref={demandPlanSectionRef} id="budget-settings-demand-plan" className="rounded-lg border border-blue-100 bg-blue-50/50 p-4">
                        <h3 className="text-sm font-semibold text-gray-900">Demand Plan</h3>
                        {budgetSource?.source?.kind === 'demandplan' ? (
                          <>
                            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-gray-600">
                              <dt>Linked plan</dt><dd className="font-medium text-gray-800">{budgetSource.source.plan?.name || 'Demand Plan'} · {budgetSource.source.plan?.publish_status === 'PUBLISHED' ? 'Published' : 'Draft'}</dd>
                              <dt>Plan updated</dt><dd>{formatDisplayDate(budgetSource.source.plan?.updated_at)}</dd>
                              <dt>Budget synced</dt><dd>{budgetSource.snapshot?.derived_at ? formatDisplayDate(budgetSource.snapshot.derived_at) : 'Not synced'}</dd>
                            </dl>
                            <p className="mt-3 text-xs leading-5 text-gray-700">Gross Sales for linked budgets always comes from the Demand Plan. Review the monthly changes and apply them explicitly; only open months change. Closed months remain frozen.</p>
                            <p className="mt-2 text-xs leading-5 text-gray-700">Discounts, refunds, and promo rebates remain fixed dollar amounts when Gross Sales changes.</p>
                            {hasEditAccess && <button type="button" onClick={onReviewChanges} className="mt-3 rounded-md border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500">Update from demand plan</button>}
                          </>
                        ) : (
                          <p className="mt-2 text-xs text-gray-600">This budget is not linked to a Demand Plan.</p>
                        )}
                      </section>
                      <section className="rounded-lg border border-gray-200 p-4">
                        <h3 className="text-sm font-semibold text-gray-900">Months and cell keys</h3>
                        <p className="mt-2 text-xs leading-5 text-gray-600">On the 10th, Eadin flags any prior month Finance hasn't closed and reports whether QuickBooks actuals are ready. Finance closes months in Budget Settings. Finance is alerted when a destination is configured; otherwise, alert delivery issues are recorded here. Closed months without a QuickBooks snapshot keep their approved forecast values and are not presented as QuickBooks actuals.</p>
                        {(budgetSource?.periods || budgetDrivers?.periods || []).filter((period) => period.actuals_lock_blocked).length > 0 && (
                          <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3">
                            <h4 className="text-xs font-semibold text-amber-900">Months awaiting Finance close</h4>
                            <ul className="mt-2 space-y-2">
                              {(budgetSource?.periods || budgetDrivers?.periods || []).filter((period) => period.actuals_lock_blocked).map((period) => (
                                <li key={period.month} className="text-xs text-amber-900">
                                  <div className="font-medium">{formatBudgetMonth(period.month)} · {period.actuals_lock_blocked.reason}</div>
                                  {period.actuals_lock_blocked.recorded_at && <div className="mt-0.5 text-[11px] text-amber-800">Recorded {formatDisplayDate(period.actuals_lock_blocked.recorded_at)}</div>}
                                  {period.actuals_lock_blocked.unmapped?.length > 0 && (
                                    <ul className="mt-1 space-y-0.5 pl-3">
                                      {period.actuals_lock_blocked.unmapped.map((account) => (
                                        <li key={`${period.month}-${account.account_number}`}>
                                          {account.account_number} {account.name} · {formatCurrency(account.amount)}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="mt-3 space-y-2 text-xs text-gray-600">
                          <p><span className="mr-2 inline-block rounded bg-indigo-50 px-2 py-1 font-semibold text-indigo-800">Actual</span>Closed month showing QuickBooks P&amp;L values. Read-only; hover to see the retained forecast.</p>
                          <p><span className="mr-2 inline-block rounded bg-gray-100 px-2 py-1 font-semibold text-gray-700">Closed</span>Locked forecast values; not QuickBooks actuals.</p>
                          <p><span className="mr-2 inline-block rounded bg-emerald-50 px-2 py-1 font-semibold text-emerald-700">Forecast</span>Open month using the approved budget values.</p>
                          <p><span className="mr-2 inline-block rounded border border-gray-200 bg-white px-2 py-1 font-semibold text-gray-700">Derived</span>Calculated from another Budget line or linked Demand Plan; change the source value.</p>
                        </div>
                      </section>
                      {hasEditAccess && <button
                        type="button"
                        className="w-full border border-gray-200 rounded-md bg-white px-3 py-2.5 flex items-center justify-between hover:bg-gray-50"
                        onClick={openCategoriesPanel}
                      >
                        <div className="text-left">
                            <div className="text-[13px] font-medium text-gray-900">Categories</div>
                            <div className="text-xs text-gray-500">Manage and reorder your budget categories</div>
                        </div>
                        <PencilIcon className="h-4 w-4 text-gray-400" />
                      </button>}

                      {(hasEditAccess || hasSheetLoadAccess) && <button
                        type="button"
                        className="w-full border border-gray-200 rounded-md bg-white px-3 py-2.5 flex items-center justify-between hover:bg-gray-50"
                        onClick={openTemplatesPanel}
                      >
                        <div className="text-left">
                            <div className="text-[13px] font-medium text-gray-900">Templates</div>
                            <div className="text-xs text-gray-500">Create and edit templates for budgets</div>
                        </div>
                        <PencilIcon className="h-4 w-4 text-gray-400" />
                      </button>}

                      {hasEditAccess && <button
                        type="button"
                        className="w-full border border-gray-200 rounded-md bg-white px-3 py-2.5 flex items-center justify-between hover:bg-gray-50"
                        onClick={openNetIncomePanel}
                      >
                        <div className="text-left">
                            <div className="text-[13px] font-medium text-gray-900">Net Income Calculation</div>
                            <div className="text-xs text-gray-500">Configure the formula for net income calculation</div>
                        </div>
                        <PencilIcon className="h-4 w-4 text-gray-400" />
                      </button>}

                      <button
                        type="button"
                        className="w-full border border-gray-200 rounded-md bg-white px-3 py-2.5 flex items-center justify-between hover:bg-gray-50"
                        onClick={() => setPanel(PANELS.DRIVERS)}
                      >
                        <div className="text-left">
                            <div className="text-[13px] font-medium text-gray-900">Drivers</div>
                            <div className="text-xs text-gray-500">Manage governed budget rates and period locks</div>
                        </div>
                        <PencilIcon className="h-4 w-4 text-gray-400" />
                      </button>
                  </div>
                )}

                {panel === PANELS.DRIVERS && (
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="text-lg font-semibold">Drivers</h2>
                    </div>
                    <div className="mb-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
                      {(() => {
                        const closedPeriods = (budgetSource?.periods || budgetDrivers?.periods || []).filter((period) => period.status === 'closed');
                        if (!closedPeriods.length) return <div>No closed months</div>;
                        const first = closedPeriods[0].month.slice(5, 7);
                        const last = closedPeriods[closedPeriods.length - 1].month.slice(5, 7);
                        const latest = closedPeriods[closedPeriods.length - 1];
                        return <div>Locked · {first === last ? first : `${first}–${last}`}/{driverYear} closed · {formatDisplayDate(latest.closed_at)} · {latest.closed_by || '—'}</div>;
                      })()}
                      <div className="mt-1">Open · editable{(budgetSource?.periods || budgetDrivers?.periods || []).find((period) => period.suggested_close && period.status !== 'closed') ? ` · ${String((budgetSource?.periods || budgetDrivers?.periods || []).find((period) => period.suggested_close && period.status !== 'closed').month).slice(5, 7)} suggested close` : ''}</div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full table-fixed border-collapse">
                        <thead>
                          <tr className="text-center text-[10px] uppercase text-gray-500">
                            <th className="w-[190px] text-left" aria-label="Driver" />
                            {DRIVER_MONTHS.map((month) => <th key={month} className="px-1 py-1">{month.toUpperCase()}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {(budgetDrivers?.definitions || []).map((definition) => {
                            const scopes = definition.scopeKind === 'channel' ? ['shopify', 'amazon'] : ['global'];
                            return scopes.map((scope) => renderDriverRow(definition, scope));
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4">
                      <div className="mb-2 text-sm font-medium text-gray-700">Periods</div>
                      <div className="grid grid-cols-6 gap-2">
                        {DRIVER_MONTHS.map((month, index) => {
                          const closed = isDriverMonthClosed(index);
                          return (
                            <div key={month} className="relative">
                              <button
                                type="button"
                                className={`w-full rounded border px-2 py-1 text-xs ${closed ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}
                                onClick={() => setPendingPeriodChange({ month, index, closed, reasons: [] })}
                              >
                                {month.toUpperCase()} {closed ? 'Closed' : 'Open'}
                              </button>
                              {pendingPeriodChange?.month === month && (
                                <div className="absolute right-0 z-20 mt-1 w-72 max-w-[calc(100vw_-_2rem)] rounded border border-gray-200 bg-white p-2 text-left shadow-lg">
                                  <div className="text-xs text-gray-700">{closed ? `Reopen ${month.toUpperCase()} ${driverYear}? Drivers and lines for that month become editable.` : `Close ${month.toUpperCase()} ${driverYear}? Drivers and lines for that month become locked.`}</div>
                                  {pendingPeriodChange.reasons?.length > 0 && (
                                    <div role="alert" className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                                      <ul className="space-y-1">
                                        {pendingPeriodChange.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                                      </ul>
                                    </div>
                                  )}
                                  <div className="mt-2 flex justify-end gap-2">
                                    <button type="button" className="text-xs text-gray-500" onClick={() => setPendingPeriodChange(null)}>Cancel</button>
                                    <button type="button" className="rounded bg-primary px-2 py-1 text-xs text-white" onClick={() => {
                                      dispatch(setBudgetPeriodStatus({
                                        budgetId,
                                        month: `${driverYear}-${String(index + 1).padStart(2, '0')}`,
                                        status: closed ? 'open' : 'closed',
                                      })).then((result) => {
                                        if (
                                          result.meta?.requestStatus === 'rejected'
                                          && result.payload?.error === 'actuals_not_ready'
                                        ) {
                                          setPendingPeriodChange((current) => (
                                            current?.month === month
                                              ? { ...current, reasons: result.payload.reasons || [] }
                                              : current
                                          ));
                                          return;
                                        }
                                        setPendingPeriodChange(null);
                                        dispatch(getBudgetDrivers({ budgetId }));
                                        dispatch(getBudgetSource(budgetId));
                                      });
                                    }}>Confirm</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {budgetDrivers?.tieOut && (
                      <div className="mt-4 rounded border border-gray-200 p-3 text-xs">
                        {(() => {
                          const drift = Object.entries(budgetDrivers.tieOut).filter(([, tieOut]) => Number(tieOut.diff) !== 0);
                          return (
                            <>
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-medium text-gray-700">{drift.length === 0 ? 'Tie-out: Gross + Discounts = plan basis 0.00 all months ✓' : `Tie-out drift: ${drift.map(([month, tieOut]) => `${month} ${tieOut.diff}`).join(', ')}`}</span>
                                <button type="button" disabled={drift.length === 0} title={drift.length === 0 ? 'Only needed if the tie-out shows drift; every save already recalculates' : undefined} className="whitespace-nowrap text-blue-600 underline disabled:text-gray-400 disabled:no-underline" onClick={async () => { await dispatch(recalculateBudgetDrivers({ budgetId })); toast.success('Budget lines recalculated.'); await dispatch(getBudgetDrivers({ budgetId })); }}>Recalculate lines from drivers</button>
                              </div>
                              <button type="button" className="mt-2 text-gray-500 underline" onClick={() => setShowTieOutDetails((value) => !value)}>{showTieOutDetails ? 'Hide details' : 'Details'}</button>
                            </>
                          );
                        })()}
                        {showTieOutDetails && (
                          <div className="mt-2">
                            <div className="grid grid-cols-5 gap-2 font-medium text-gray-500">
                              <span>Month</span><span>True Gross</span><span>Discounts</span><span>Basis</span><span>diff</span>
                            </div>
                            {Object.entries(budgetDrivers.tieOut).map(([month, tieOut]) => (
                              <div key={month} className="grid grid-cols-5 gap-2">
                                <span>{month}</span>
                                <span>{tieOut.true_gross}</span>
                                <span>{tieOut.discounts}</span>
                                <span>{tieOut.basis}</span>
                                <span>{tieOut.diff}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {panel === PANELS.CATEGORIES && (
                  <div>
                      <div className="flex justify-between items-center mb-2">
                        <h2 className="text-lg font-semibold">Categories</h2>
                        <button
                            onClick={() => setAddCategoryModalOpen(true)}
                            className="text-base bg-primary text-white px-3 py-1 rounded-md hover:bg-gray-800 transition-colors"
                        >
                            Add Category
                        </button>
                      </div>
                      <div className="text-sm text-gray-500 mb-4">
                        <span className="text-sm text-gray-500 block">
                          Categories are used to group and organize your budget line items.
                        </span> 
                        <span className="text-sm text-gray-500 block">
                          Ordering categories affects the order of all budgets.
                        </span>
                      </div>
                      <div className="flex flex-col gap-3">
                      {arrayHasItems(categoriesToRender) &&
                          categoriesToRender.map((category, index) => (
                            <EditableCategoryName
                                key={category.id}
                                category={category}
                                index={index}
                                hasEditAccess={hasEditAccess}
                                moveCategory={moveCategory}
                                onDrop={persistOrderIfChanged}
                                onDeleteClick={handleDeleteClick}
                                onDeleteDisabledClick={handleDeleteDisabledClick}
                                onCategoryUpdate={onCategoryChange}
                            />
                          ))}
                      </div>
                  </div>
                )}

                {panel === PANELS.TEMPLATES && (
                  <div>
                      <div className="flex justify-between items-center mb-2">
                        <h2 className="text-lg font-semibold">Templates</h2>
                        <div className="flex gap-2">
                        {hasSheetLoadAccess && <button
                            onClick={() => setSheetLoadOpen(true)}
                            className="text-sm border border-gray-300 px-3 py-1 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Load from Sheet
                        </button>}
                        {hasEditAccess && <button
                            onClick={openCreateTemplateEditor}
                            className="text-base bg-primary text-white px-3 py-1 rounded-md hover:bg-gray-800 transition-colors"
                        >
                            Add Template
                        </button>}
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-4">
                        Templates are used to create new budgets.
                      </p>
                      <div className="flex flex-col gap-3">
                      {arrayHasItems(templatesToRender) &&
                          templatesToRender.map((template) => (
                          <EditableTemplateName
                              key={template.id}
                              template={template}
                              hasEditAccess={hasEditAccess}
                              onEditClick={openEditTemplateEditor}
                              onDeleteClick={handleDeleteTemplateClick}
                              isDeleting={budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.deleteTemplate)}
                          />
                          ))}
                      </div>
                  </div>
                )}

                {panel === PANELS.TEMPLATE_EDITOR && (
                  <TemplateCreateEditSidebar
                      embedded={true}
                      isOpen={true}
                      onClose={closeTemplateEditor}
                      mode={templateEditorMode}
                      template={templateToEdit}
                      categoriesToRender={budgetSettings?.categories || []}
                      onSave={handleSaveTemplate}
                      isSaving={isSavingTemplate}
                      onRegisterSubmit={(fn) => setTemplateEditorSubmit(() => fn)}
                      onCanSaveChange={setTemplateEditorCanSave}
                  />
                )}

                {panel === PANELS.NET_INCOME && (
                  <div>
                      <div className="flex justify-between items-center mb-2">
                        <h2 className="text-lg font-semibold">Net Income Calculation</h2>
                        <button
                            onClick={openCreateNetIncomeEditor}
                            className="text-base bg-primary text-white px-3 py-1 rounded-md hover:bg-gray-800 transition-colors"
                        >
                            Add Calculation
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 mb-4">
                        Create and edit named formulas used to calculate net income.
                      </p>
                      <div className="flex flex-col gap-3">
                        {arrayHasItems(netIncomeCalculationsToRender) &&
                          netIncomeCalculationsToRender.map((calc) => (
                            <EditableNetIncomeName
                              key={calc.id}
                              calculation={calc}
                              hasEditAccess={hasEditAccess}
                              onEditClick={openEditNetIncomeEditor}
                              onDeleteClick={handleDeleteNetIncomeClick}
                            onDeleteDisabledClick={handleDeleteNetIncomeDisabledClick}
                              isDeleting={budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.deleteNetIncome)}
                            />
                          ))}
                      </div>
                  </div>
                )}

                {panel === PANELS.NET_INCOME_EDITOR && (
                  <NetIncomeCreateEditSidebar
                      embedded={true}
                      isOpen={true}
                      onClose={closeNetIncomeEditor}
                      mode={netIncomeEditorMode}
                      calculation={netIncomeToEdit}
                      categoriesToRender={budgetSettings?.categories || []}
                      onSave={handleSaveNetIncome}
                      isSaving={isSavingNetIncome}
                      onRegisterSubmit={(fn) => setNetIncomeEditorSubmit(() => fn)}
                      onCanSaveChange={setNetIncomeEditorCanSave}
                  />
                )}
            </div>
            {/* Modals */}
            <ConfrimModal
                isOpen={deleteModalOpen}
                title={`Are you sure you want to delete the category \"${categoryToDelete?.name}\"?`}
                confirmButtonTitle="Delete"
                isLoading={budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.deleteBudgetCategory)}
                submitClick={handleConfirmDelete}
                cancelClick={handleCancelDelete}
            />
            <CategoryDeleteInfoModal
                isOpen={deleteInfoModalOpen}
                onClose={handleCloseInfoModal}
                categoryName={categoryToDelete?.name}
                budgets={categoryToDelete?.budgets || []}
                isUsedInForecastSettings={Boolean(categoryToDelete?.is_used_in_forecast_settings)}
            />
            <NetIncomeDeleteInfoModal
                isOpen={deleteNetIncomeInfoModalOpen}
                onClose={handleCloseNetIncomeInfoModal}
                calculationName={netIncomeToDelete?.name}
                budgets={netIncomeToDelete?.budgets || []}
            />
            <AddCategoryModal
                isOpen={addCategoryModalOpen}
                onClose={() => setAddCategoryModalOpen(false)}
                onSubmit={handleCreateCategory}
                isLoading={budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.createBudgetCategory)}
            />
            <ConfrimModal
                isOpen={deleteTemplateModalOpen}
                title={`Are you sure you want to delete the template \"${templateToDelete?.name}\"?`}
                confirmButtonTitle="Delete"
                isLoading={budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.deleteTemplate)}
                submitClick={handleConfirmDeleteTemplate}
                cancelClick={handleCancelDeleteTemplate}
            />
            <ConfrimModal
                isOpen={deleteNetIncomeModalOpen}
                title={`Are you sure you want to delete the net income calculation \"${netIncomeToDelete?.name}\"?`}
                confirmButtonTitle="Delete"
                isLoading={budgetSettingsLoadingKeys.includes(BudgetLoadingKeys.deleteNetIncome)}
                submitClick={handleConfirmDeleteNetIncome}
                cancelClick={handleCancelDeleteNetIncome}
            />
            <BudgetTemplateSheetLoadModal
                isOpen={sheetLoadOpen}
                canEdit={hasSheetLoadEditAccess}
                budgetId={budgetId}
                budgetCards={budgetCards || []}
                categories={categoriesToRender}
                netIncomeCalculations={netIncomeCalculationsToRender}
                onClose={() => setSheetLoadOpen(false)}
                onSaved={async () => {
                  await dispatch(getBudgetSettings({}));
                  await dispatch(getBudgetCards({}));
                }}
            />
        </div>
      </Drawer>
    </DndProvider>
  );
};
